import { Mark } from "@tiptap/core";
import { mergeAttributes } from "@tiptap/core";
import { getMarksBetween } from "@tiptap/react";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import { Selection } from "prosemirror-state";

export interface IMarker {
  id: string;
  color: string;
}

interface ISelection {
  from: number;
  to: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    marker: {
      setMarker: (selection: ISelection, marker: IMarker) => ReturnType;

      removeMarker: (id: string) => ReturnType;

      updateAllMarkers: (markers: IMarker[]) => ReturnType;
    };
  }
}

const Marker = Mark.create<
  {
    onMarkerSelected?: (id: string) => void;
  } | null,
  { markers: IMarker[] }
>({
  name: "marker",

  inclusive: false,

  addAttributes() {
    return {
      id: {
        default: null,
      },
      color: {
        default: null,
      },
    };
  },

  renderHTML({ HTMLAttributes, mark }) {
    const elem = document.createElement("span");

    Object.entries(
      mergeAttributes(HTMLAttributes, {
        class: clsxm([
          "marker",
          mark.attrs.color && `bg-${mark.attrs.color}-50a`,
        ]),
      })
    ).forEach(([attr, val]) => elem.setAttribute(attr, val));

    elem.addEventListener("click", () => {
      this.options?.onMarkerSelected?.(mark.attrs.id);
    });

    return elem;
  },

  addCommands() {
    return {
      setMarker:
        (selection: ISelection, marker: IMarker) =>
        ({ chain }) =>
          chain().setTextSelection(selection).setMark("marker", marker).run(),
      removeMarker:
        (id: string) =>
        ({ editor, chain }) => {
          const start = Selection.atStart(editor.state.doc);
          const end = Selection.atEnd(editor.state.doc);

          const { from, to } = editor.state.selection;

          const chainInstance = chain();

          getMarksBetween(start.from, end.to, editor.state.doc).forEach(
            ({ mark, from, to }) => {
              if (mark.type.name === "marker" && mark.attrs.id === id) {
                chainInstance
                  .setTextSelection({ from, to })
                  .unsetMark(mark.type);
              }
            }
          );

          return chainInstance.setTextSelection({ from, to }).run();
        },
      updateAllMarkers:
        (markers: IMarker[]) =>
        ({ chain, editor }) => {
          const start = Selection.atStart(editor.state.doc);
          const end = Selection.atEnd(editor.state.doc);

          const { from, to } = editor.state.selection;

          const chainInstance = chain();

          getMarksBetween(start.from, end.to, editor.state.doc).forEach(
            ({ mark, from, to }) => {
              if (mark.type.name === "marker") {
                const marker =
                  markers &&
                  markers.find((marker) => marker.id === mark.attrs.id);
                const color = marker ? marker.color : "";

                if (markers)
                  chainInstance
                    .setTextSelection({ from, to })
                    .updateAttributes(mark.type, { color });
              }
            }
          );

          return chainInstance.setTextSelection({ from, to }).run();
        },
    };
  },
});

export default Marker;
